// La page profil — huitième écran migré.
//
// Elle réunit tout ce que les précédents ont éprouvé, et une chose de plus :
// des données qu'il faut aller CHERCHER avant de savoir quoi afficher. Le
// membre, ses réglages de confidentialité, l'état de mon blocage à son égard —
// trois lectures, dont deux ne concernent que les profils des autres.
//
// Les publications viennent de `ListePublications`, qui rend de vraies cartes
// React depuis que `posts.js` a été traduit.

import { useEffect, useState } from "react";
import Store, { lireUneFois } from "../../store.js";
import { toast, modale, apercuImage, confirmer } from "../../ui.js";
import { etatAmitie } from "../../store.js";
import { confidentialiteDe } from "../../securite.js";
import { amisEnCommunTexte } from "../../algorithmes.js";
import { estModerateur, estSuspendu, suspendreMembre, supprimerCompteMembre } from "../../views/moderation.js";
import { renommerMembre } from "../../views/moderation-outils.js";
import { bloquerMembre, debloquerMembre } from "../../views/blocages.js";
import { signaler } from "../../views/partage.js";
import { editerProfil } from "../../views/profil-editer.js";
// Importés directement, et non par `await import("views/stories.js")` comme
// avant : ce chargement différé n'avait de sens que tant que les statuts
// étaient un gros morceau de HTML à part. Maintenant qu'ils sont en React,
// il forçait le regroupeur à sortir React lui-même dans un second fichier,
// téléchargé au démarrage malgré tout — deux allers-retours pour rien.
import { composerStory } from "../../views/story-composer.js";
import { ouvrirStatutsDe } from "../Visionneuse.jsx";
import Avatar from "../Avatar.jsx";
import Certifie from "../Certifie.jsx";
import Presence from "../Presence.jsx";
import Icone from "../Icone.jsx";
import TexteRiche from "../TexteRiche.jsx";
import BoutonsAmitie from "../BoutonsAmitie.jsx";
import ListePublications, { postsDeMembre } from "../ListePublications.jsx";
import Composeur, { ComposeurBloque } from "../Composeur.jsx";
import Vide, { Chargement } from "../Vide.jsx";
import { useAbonnement, useStatuts, useSignaux } from "../hooks.js";

const moisEtAnnee = (ts) =>
  new Date(ts || Date.now()).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

/* ------------------------------------------------------------------ photos */

/**
 * Changer sa photo de profil ou sa couverture d'un seul geste, comme sur
 * Facebook : l'appareil photo ouvre la galerie, sans passer par le formulaire.
 */
function useChangerPhoto(ctx) {
  const [envoi, setEnvoi] = useState("");

  const choisir = (quoi) => {
    const champ = document.createElement("input");
    champ.type = "file";
    champ.accept = "image/*";
    champ.onchange = async () => {
      const f = champ.files?.[0];
      if (!f) return;
      setEnvoi(quoi);
      toast(quoi === "photo" ? "Envoi de la photo…" : "Envoi de la couverture…");
      try {
        const url = await Store.upload(f, "image");
        const patch = quoi === "photo" ? { photo: url } : { couverture: url };
        await Store.users.update(patch);
        ctx.majMoi(patch); // redessine la coquille ET la page
        toast(quoi === "photo" ? "Photo de profil mise à jour" : "Photo de couverture mise à jour");

        // Une nouvelle photo paraît dans le fil : c'est un moment de la vie du
        // membre, et c'est ce qui suscite les réactions.
        //
        // Un échec ICI ne remet pas la photo en cause — elle est enregistrée.
        // Mais il ne doit plus être avalé : il l'était, et le membre voyait
        // « Photo de profil mise à jour » sans que rien ne paraisse dans le fil,
        // sans un mot pour l'expliquer. Les deux causes réelles :
        //   · un compte dont l'attestation n'est pas encore validée n'a pas le
        //     droit de publier — la photo change, l'annonce non ;
        //   · deux changements coup sur coup butent sur la limite de cadence.
        // Dans les deux cas le membre doit savoir POURQUOI.
        try {
          await Store.posts.add({
            type: quoi === "photo" ? "photo_profil" : "photo_couverture",
            authorId: ctx.moi.uid,
            auteurNom: ctx.moi.nom,
            auteurPhoto: quoi === "photo" ? url : ctx.moi.photo || "",
            texte: "",
            image: url,
          });
        } catch (err) {
          toast(
            `Photo enregistrée, mais non annoncée dans le fil : ${err?.message || "publication refusée"}`,
            "erreur"
          );
        }
      } catch (e) {
        toast(e.message || "Envoi impossible", "erreur");
      } finally {
        setEnvoi("");
      }
    };
    champ.click();
  };

  return { choisir, envoi };
}

/**
 * Toucher la photo de profil de quelqu'un qui a un statut en cours pose une
 * question : veut-on voir sa photo, ou son statut ? Facebook la pose aussi —
 * deviner à sa place se trompe une fois sur deux.
 */
function ouvrirPhotoOuStatut(u, id, ctx, aStatut) {
  if (!aStatut) {
    if (!u.photo) return toast("Ce membre n'a pas encore de photo de profil");
    return apercuImage(u.photo, u.nom);
  }
  const { el, fermer } = modale({
    titre: u.nom,
    corps: `<div class="menu-liste">
      ${u.photo ? `<button class="menu-item" data-x="photo">Voir la photo de profil</button>` : ""}
      <button class="menu-item" data-x="statut">Voir le statut</button>
    </div>`,
  });
  el.addEventListener("click", async (e) => {
    const b = e.target.closest("[data-x]");
    if (!b) return;
    fermer();
    if (b.dataset.x === "photo") return apercuImage(u.photo, u.nom);
    ouvrirStatutsDe(id, ctx);
  });
}

/* --------------------------------------------------------------- abonnés */

/**
 * Les abonnés d'un membre : combien ils sont, et si j'en fais partie.
 *
 * ⚠️ On n'écoute PAS tout le tiroir. Il est sans limite de nombre — c'est une
 * audience, pas un cercle — et le télécharger pour le compter coûterait une
 * lecture par abonné, à chaque ouverture de la page. On écoute donc le seul
 * document qui me concerne, et on demande le nombre au serveur, qui sait
 * compter sans rien envoyer.
 *
 * `ouvert` vient du réglage de l'intéressé. Éteint — le cas de tout le monde
 * tant que personne ne l'a coché — rien n'est demandé du tout.
 */
function useAbonnes(uid, ouvert) {
  const dispo = ouvert && Boolean(Store.abonnes);
  const [nombre, setNombre] = useState(null);
  const [tour, setTour] = useState(0);

  const suivi = useAbonnement(
    (cb) => (dispo ? Store.abonnes.subscribeSuivi(uid, cb) : (cb(false), undefined)),
    false,
    [uid, dispo]
  );

  useEffect(() => {
    if (!dispo) return setNombre(null);
    let vivant = true;
    // Le nombre est un ornement : s'il manque, on ne l'affiche pas, et on ne
    // fait surtout pas de bruit pour ça.
    Store.abonnes.compter(uid).then(
      (n) => vivant && setNombre(n),
      () => vivant && setNombre(null)
    );
    return () => { vivant = false; };
  }, [uid, dispo, tour]);

  return { nombre, suivi, dispo, recompter: () => setTour((n) => n + 1) };
}

/**
 * « S'abonner » — à côté d'« Ajouter en ami », et volontairement plus discret.
 *
 * Ce n'est pas une demi-amitié : c'est à sens unique, sans acceptation.
 * ⚠️ Depuis la onzième passe (24 octobre 2026), c'est la CLÉ qui ouvre les
 * publications publiques d'un non-ami — dans le fil comme sur son profil.
 * Jamais ses réservées : le serveur le garantit.
 */
function BoutonAbonner({ uid, nom, ctx, abonnes }) {
  const [enCours, setEnCours] = useState(false);

  const cliquer = async () => {
    if (enCours) return;
    setEnCours(true);
    try {
      if (abonnes.suivi) {
        await Store.abonnes.desabonner(uid);
        toast(`Vous ne suivez plus ${nom || "ce membre"}`);
      } else {
        await Store.abonnes.abonner(uid);
        toast(`Vous suivez ${nom || "ce membre"}`);
        Store.notifs.add(uid, {
          type: "abonne", deId: ctx.moi.uid, deNom: ctx.moi.nom, dePhoto: ctx.moi.photo || "",
        });
      }
      abonnes.recompter();
    } catch (e) {
      toast(e.message || "Action impossible", "erreur");
    }
    setEnCours(false);
  };

  return (
    <button
      className={`btn ${abonnes.suivi ? "btn--fantome" : "btn--gris"}`}
      disabled={enCours}
      onClick={cliquer}
    >
      {abonnes.suivi ? "✓ Abonné" : "S'abonner"}
    </button>
  );
}

/* ----------------------------------------------------------------- l'entête */

function Actions({ u, id, ctx, estMoi, estBloque, relations, detailsVisibles, abonnes }) {
  const redessiner = () => ctx.majMoi({}); // seul moyen propre de rafraîchir une fiche

  /**
   * La certification n'est pas une récompense : elle dit « cette personne est
   * bien celle qu'elle dit être ». Seule la modération la pose — ici ET dans
   * les règles du serveur, car un sceau qu'un membre pourrait s'accorder
   * lui-même ne prouverait rien.
   *
   * Le bouton s'affiche aussi sur SA PROPRE fiche : c'est bien un modérateur qui
   * agit, et le premier compte à certifier est justement celui du responsable.
   */
  const boutonCertifier = estModerateur(ctx.moi) && (
    <button
      className="btn btn--gris"
      onClick={async () => {
        const oui = !u.certifie;
        if (
          !oui &&
          !(await confirmer("Retirer la certification ?", `Le sceau disparaîtra du nom de ${u.nom}.`, "Retirer"))
        )
          return;
        try {
          await Store.moderation.certifier(id, oui);
          toast(oui ? "Membre certifié ✔️" : "Certification retirée");
          // sur sa propre fiche, il faut aussi rafraîchir ce que l'app sait de moi
          if (estMoi) ctx.majMoi({ certifie: oui || undefined });
          else redessiner();
        } catch (e) {
          toast(e.message || "Action impossible", "erreur");
        }
      }}
    >
      {u.certifie ? "Retirer la certification" : "Certifier ce membre"}
    </button>
  );

  if (estMoi)
    return (
      <div className="profil__actions">
        <button
          className="btn btn--primaire"
          onClick={() => composerStory(ctx)}
        >
          <Icone nom="plus" taille={17} /> Ajouter un statut
        </button>
        <button className="btn btn--gris" onClick={() => editerProfil(ctx)}>
          <Icone nom="appareil" taille={17} /> Modifier le profil
        </button>
        {boutonCertifier}
      </div>
    );

  return (
    <div className="profil__actions">
      <BoutonsAmitie
        etat={etatAmitie(relations, ctx.moi.uid, id)}
        uid={id}
        nom={u.nom}
        petit={false}
        ctx={ctx}
      />
      {/* À côté d'« Ajouter en ami », et seulement chez qui l'a autorisé. */}
      {abonnes.dispo && !estBloque && (
        <BoutonAbonner uid={id} nom={u.nom} ctx={ctx} abonnes={abonnes} />
      )}
      <button
        className="btn btn--gris"
        onClick={async () => (location.hash = `#/messages/${await Store.convos.ensure(id)}`)}
      >
        <Icone nom="messages" taille={17} /> Message
      </button>
      <button
        className="btn btn--gris btn--carre"
        aria-label="Signaler"
        onClick={() => signaler({ cibleType: "profil", cibleId: id, auteurId: id, apercu: u.nom })}
      >
        <Icone nom="options" taille={18} />
      </button>
      {estBloque ? (
        <button className="btn btn--gris" onClick={() => debloquerMembre(u, redessiner)}>
          Débloquer
        </button>
      ) : (
        <button className="btn btn--gris" onClick={() => bloquerMembre(u, redessiner)}>
          Bloquer
        </button>
      )}
      {boutonCertifier}
      {/* Renommer : la régularisation du nom réel (24 octobre 2026). Sur le
          profil des AUTRES — le sien se change par l'édition ordinaire. */}
      {estModerateur(ctx.moi) && id !== ctx.moi.uid && !u.supprime && (
        <button className="btn btn--gris" onClick={() => renommerMembre(u, redessiner)}>
          Renommer
        </button>
      )}
      {estModerateur(ctx.moi) &&
        (estSuspendu(u) ? (
          <button
            className="btn btn--gris"
            onClick={async () => {
              await Store.moderation.reactiver(id);
              toast("Membre réactivé");
              redessiner();
            }}
          >
            Réactiver
          </button>
        ) : (
          <button className="btn btn--danger" onClick={() => suspendreMembre(u, redessiner)}>
            Suspendre
          </button>
        ))}
      {/* ⚠️ RÉSERVÉ AUX ADMINISTRATEURS, et non à toute la modération.
          Suspendre se rattrape ; supprimer, non. Un modérateur peut donc
          restreindre, seul un admin peut effacer. */}
      {ctx.moi?.role === "admin" && id !== ctx.moi.uid && !u.supprime && (
        <button className="btn btn--danger" onClick={() => supprimerCompteMembre(u, redessiner)}>
          Supprimer définitivement
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- écran */

/**
 * La carte « Amis » — comme Facebook.
 *
 * ⚠️ PAS une liste verticale. La première version en était une, et elle ne
 * tenait pas debout : à cinq cents amis, on faisait défiler l'écran pendant
 * une minute avant d'atteindre les publications. Une carte doit tenir dans un
 * regard.
 *
 * Donc : le nombre, neuf vignettes, et un lien vers la liste entière. Neuf
 * parce que trois par trois remplit exactement la largeur d'un téléphone sans
 * laisser de trou.
 *
 * Les données viennent de `users/{uid}/amis` — un document par ami, rangé sous
 * son propriétaire. Ce détour est ce qui permet au SERVEUR de vérifier qui a le
 * droit de regarder : dans la collection commune, la règle ne peut pas savoir
 * de qui l'on consulte la liste, ce nom étant dans la question et non dans le
 * document.
 */
function CarteAmis({ ctx, uid }) {
  const ids = useAbonnement((cb) => Store.amis.subscribeAmisDe(uid, cb), undefined, [uid]);
  const [gens, setGens] = useState([]);

  useEffect(() => {
    if (!Array.isArray(ids) || !ids.length) return setGens([]);
    let vivant = true;
    Promise.all(ids.slice(0, 9).map((u) => ctx.profil(u).catch(() => null))).then(
      (us) => vivant && setGens(us.filter(Boolean))
    );
    return () => { vivant = false; };
  }, [ids, ctx]);

  // `null` veut dire « refusé par le serveur » — une réponse légitime, pas une
  // panne : ce membre ne montre ses amis qu'à ses amis. On le dit sans alarmer.
  if (ids === null)
    return (
      <section className="carte">
        <h2 className="titre-page">Amis</h2>
        <p className="texte-doux petit">🔒 Ce membre ne montre ses amis qu'à ses amis.</p>
      </section>
    );
  if (ids === undefined || (ids.length && !gens.length)) return null;
  if (!ids.length) return null;

  return (
    <section className="carte">
      <div className="entete-page">
        <h2 className="titre-page">
          Amis <span className="texte-doux">· {ids.length}</span>
        </h2>
        {/* Pas encore de lien « voir tous » : l'écran n'existe pas, et le
            routeur renverrait au fil — un lien qui trompe est pire qu'un lien
            absent. On dit simplement combien il en reste. */}
        {ids.length > 9 && (
          <span className="texte-doux petit">+{ids.length - 9} autres</span>
        )}
      </div>
      <div className="grille-amis">
        {gens.map((u) => {
          /* La forme COURTE — « 3 en commun » — parce qu'une vignette fait un
             tiers de la largeur d'un téléphone : la phrase entière y passerait
             sur trois lignes et pousserait les visages hors de la carte. */
          const communs = amisEnCommunTexte(ctx.signaux?.instantane(), u.uid, true);
          return (
            <a className="vignette-ami" href={`#/profil/${u.uid}`} key={u.uid}>
              <Avatar membre={u} taille={96} carre />
              <span className="vignette-ami__nom">{u.nom}</span>
              {communs && <span className="vignette-ami__communs">{communs}</span>}
            </a>
          );
        })}
      </div>
    </section>
  );
}

/**
 * La carte « Photos ».
 *
 * Les photos ne sont pas rangées à part : elles vivent dans les publications.
 * On les y récolte — `image` pour les anciennes, `images` pour les albums — et
 * on n'en montre que neuf. Une carte est un aperçu, pas une galerie.
 */
function CartePhotos({ ctx, uid }) {
  const posts = useAbonnement((cb) => Store.posts.subscribeByAuthor(uid, cb), null, [uid]);
  if (!posts) return null;

  const photos = posts
    .flatMap((p) => (p.images?.length ? p.images : p.image ? [p.image] : []))
    .filter(Boolean);
  if (!photos.length) return null;

  return (
    <section className="carte">
      <div className="entete-page">
        <h2 className="titre-page">
          Photos <span className="texte-doux">· {photos.length}</span>
        </h2>
      </div>
      <div className="grille-photos">
        {photos.slice(0, 9).map((u, i) => (
          <button
            type="button"
            className="grille-photos__case"
            key={u + i}
            onClick={() => apercuImage(u)}
            aria-label={`Photo ${i + 1}`}
          >
            <img src={u} alt="" loading="lazy" />
          </button>
        ))}
      </div>
    </section>
  );
}

export default function Profil({ ctx, params }) {
  const id = params.id || ctx.moi.uid;
  const estMoi = id === ctx.moi.uid;

  // `chargement` reste vrai jusqu'à ce qu'on sache tout ce qu'il faut pour
  // dessiner : le membre, ce qu'il accepte de montrer, et si je l'ai bloqué.
  const [etat, setEtat] = useState({ phase: "chargement" });
  const relations = useAbonnement((cb) => Store.amis.subscribe(cb), []);
  // La présence rend désormais { enLigne, vuA } — on ne garde ici que les
  // présents. La date de dernière connexion se lit par `<Presence>`, qui la
  // met en mots au même endroit pour tous les écrans.
  const presence = useAbonnement((cb) => Store.presence.subscribe(cb), null);
  const enLigne = presence?.enLigne || new Set();
  const avecStatut = useStatuts(ctx);
  // Le voisinage — les amis de mes amis — se calcule après coup, en plusieurs
  // fois. Sans cet abonnement, « N amis en commun » resterait figé sur le
  // premier rendu, c'est-à-dire sur zéro.
  const versionSignaux = useSignaux(ctx.signaux);
  const photo = useChangerPhoto(ctx);
  // Le réglage vient de la fiche qu'on est en train de charger : tant qu'elle
  // n'est pas là, rien n'est demandé — et chez qui n'a pas ouvert ses abonnés,
  // rien ne le sera jamais.
  const abonnes = useAbonnes(id, confidentialiteDe(etat.u).abonnes === true);

  useEffect(() => {
    let vivant = true;
    setEtat({ phase: "chargement" });
    (async () => {
      const u = estMoi ? ctx.moi : await Store.users.get(id);
      if (!vivant) return;
      if (!u) return setEtat({ phase: "introuvable" });

      // Sur son propre profil il n'y a rien à demander : on se montre tout.
      if (estMoi) return setEtat({ phase: "prete", u, detailsVisibles: true, estBloque: false });

      const bloques = await lireUneFois((cb) => Store.blocages.subscribeMiens(cb));
      // « Qui peut voir mon église, ma ville et mes amis. » Le nom et la photo
      // restent visibles : sans eux, on ne reconnaîtrait plus personne.
      let detailsVisibles = true;
      if (confidentialiteDe(u).details === "amis") {
        const rs = await lireUneFois((cb) => Store.amis.subscribe(cb));
        detailsVisibles = etatAmitie(rs, ctx.moi.uid, id) === "amis";
      }
      if (vivant)
        setEtat({ phase: "prete", u, detailsVisibles, estBloque: bloques.includes(id) });
    })();
    return () => {
      vivant = false;
    };
  }, [id, estMoi, ctx]);

  if (etat.phase === "chargement")
    return (
      <div className="colonne">
        <Chargement texte="Chargement du profil…" />
      </div>
    );

  if (etat.phase === "introuvable")
    return (
      <div className="colonne">
        <Vide symbole="👤" titre="Membre introuvable" texte="Ce compte n'existe plus." />
      </div>
    );

  const { u, detailsVisibles, estBloque } = etat;
  const depuis = moisEtAnnee(u.createdAt);
  const nbAmis = relations.filter((r) => r.etat === "acceptee").length;
  // Combien de MES amis sont aussi les siens. Déjà calculé par les signaux pour
  // l'annuaire : ici, c'est une simple lecture de la carte.
  /* ⚠️ `amisEnCommun` ET NON `amisDeMesAmis`. Le second écarte volontairement
     ceux qui sont déjà mes amis — c'est une carte faite pour SUGGÉRER. Or c'est
     justement sur le profil d'un ami que « 3 amis en commun » a le plus de
     sens. Voir la note des deux cartes dans `js/signaux.js`. */
  const amisCommuns = ctx.signaux?.instantane().amisEnCommun?.get(id) || 0;
  void versionSignaux; // ne sert qu'à redessiner quand le voisinage a bougé

  return (
    <div className="colonne">
      <section className="profil">
        <div
          className={`profil__couverture${u.couverture ? " est-cliquable" : ""}`}
          style={u.couverture ? { backgroundImage: `url('${u.couverture}')` } : undefined}
          onClick={() => u.couverture && apercuImage(u.couverture, `Couverture de ${u.nom}`)}
        >
          {estMoi && (
            <button
              className="profil__bouton-photo profil__bouton-photo--couv"
              aria-label="Modifier la photo de couverture"
              disabled={photo.envoi === "couverture"}
              onClick={(e) => {
                // le bouton est PAR-DESSUS la couverture cliquable : sans cela,
                // vouloir la changer ouvrirait son aperçu en plein écran
                e.stopPropagation();
                photo.choisir("couverture");
              }}
            >
              <Icone nom="appareil" taille={18} />
              <span>Couverture</span>
            </button>
          )}
        </div>

        <div className="profil__corps">
          <div className="profil__photo">
            <button
              className="avatar-enveloppe avatar-bouton"
              aria-label="Voir la photo de profil"
              onClick={() => ouvrirPhotoOuStatut(u, id, ctx, avecStatut.has(id))}
            >
              <Avatar membre={u} taille={132} anneau={avecStatut.has(id)} />
              {!estMoi && enLigne.has(id) && <span className="point-vert point-vert--sur-avatar" />}
            </button>
            {estMoi && (
              <button
                className="profil__bouton-photo profil__bouton-photo--avatar"
                aria-label="Modifier la photo de profil"
                disabled={photo.envoi === "photo"}
                onClick={() => photo.choisir("photo")}
              >
                <Icone nom="appareil" taille={19} />
              </button>
            )}
          </div>

          <h1>
            {u.nom}
            <Certifie membre={u} taille={22} />
          </h1>
          {/* Sous le nom, tout de suite : c'est la première chose qu'on cherche
              avant d'écrire à quelqu'un. */}
          <Presence ctx={ctx} uid={u.uid} />
          {u.bio && (
            <p className="profil__bio">
              <TexteRiche texte={u.bio} />
            </p>
          )}
          <p className="profil__meta">
            {/* Le nombre d'amis : le mien est calculé de mes relations. Celui
                d'un autre demanderait de lire les siennes — et son réglage de
                confidentialité peut l'interdire. On ne l'affiche donc pas. */}
            {estMoi && (
              <span>
                {nbAmis} ami{nbAmis > 1 ? "s" : ""} ·{" "}
              </span>
            )}
            {/* Les AMIS EN COMMUN, chez les autres seulement — chez soi, la
                question n'a pas de sens.
                Le nombre ne coûte aucune lecture : `ctx.signaux` tient déjà le
                voisinage à jour pour l'annuaire et les suggestions. Et il ne
                révèle rien qui ne le soit déjà : ce sont MES amis que je
                compte, pas les siens. C'est d'ailleurs pour cela qu'on peut
                l'afficher là où le nombre total de ses amis, lui, reste caché
                — voir la note juste au-dessus. */}
            {!estMoi && amisCommuns > 0 && (
              <span>
                {amisCommuns} ami{amisCommuns > 1 ? "s" : ""} en commun ·{" "}
              </span>
            )}
            {/* Les abonnés, eux, se comptent pour tout le monde : c'est une
                audience publique, et c'est tout son intérêt. Le nombre ne
                paraît que chez qui a ouvert le réglage. */}
            {abonnes.nombre !== null && (
              <span>
                {abonnes.nombre} abonné{abonnes.nombre > 1 ? "s" : ""} ·{" "}
              </span>
            )}
            Membre depuis {depuis}
          </p>

          <Actions
            u={u}
            id={id}
            ctx={ctx}
            estMoi={estMoi}
            estBloque={estBloque}
            relations={relations}
            detailsVisibles={detailsVisibles}
            abonnes={abonnes}
          />
        </div>
      </section>

      <section className="carte profil__details">
        <h2 className="profil__titre">Détails</h2>
        <ul className="details">
          {detailsVisibles && u.eglise && (
            <li>
              <Icone nom="membres" taille={19} /> Église : <strong>{u.eglise}</strong>
            </li>
          )}
          {detailsVisibles && u.ville && (
            <li>
              <Icone nom="fil" taille={19} /> Habite à <strong>{u.ville}</strong>
            </li>
          )}
          <li>
            <Icone nom="alertes" taille={19} /> Membre depuis {depuis}
          </li>
          {!detailsVisibles && (
            <li className="texte-doux">
              <Icone nom="options" taille={19} /> Ce membre réserve ses informations à ses amis.
            </li>
          )}
        </ul>
        {estMoi && (
          <a className="btn btn--gris btn--bloc" href="#/menu">
            <Icone nom="menu" taille={17} /> Paramètres et raccourcis
          </a>
        )}
      </section>

      {/* Amis et Photos AVANT les publications — c'est ce qu'on vient chercher
          sur un profil, et cela évite de faire défiler tout un fil pour y
          accéder. Chacune se retire d'elle-même si elle n'a rien à montrer. */}
      <CarteAmis ctx={ctx} uid={id} />
      <CartePhotos ctx={ctx} uid={id} />

      {/* Publier depuis son propre profil, comme sur Facebook.
          ⚠️ Sur SON profil seulement : une boîte de saisie sur la page de
          quelqu'un d'autre laisserait croire qu'on écrit CHEZ lui — ce que
          Koinonia ne permet pas, et que le serveur refuserait de toute façon
          (une publication porte l'identité de son auteur, jamais celle de la
          page où elle a été écrite). */}
      {estMoi &&
        (ctx.moi?.emailVerifie !== false && !estSuspendu(ctx.moi) ? (
          <Composeur ctx={ctx} />
        ) : (
          <ComposeurBloque moi={ctx.moi} />
        ))}

      {/* ⚠️ LE TITRE VIENT ICI, ET PAS PLUS HAUT. Il était posé avant les cartes
          Amis et Photos : il les coiffait donc toutes les deux, et annonçait que
          la liste des amis faisait partie des publications. Un titre ne nomme
          que ce qui le suit — ici, la liste, et rien d'autre. */}
      <h2 className="titre-section">{estMoi ? "Mes publications" : "Ses publications"}</h2>

      <ListePublications ctx={ctx} {...postsDeMembre(id)} deps={[id]} />
    </div>
  );
}
